import { Get } from "@repo/utils/api";
import FullName from "@repo/utils/FullName";
import UnreachableCaseError from "@repo/utils/UnreachableCaseError";
import UProxy from "@repo/utils/UProxy";
import FeatureStore from "@repo/utils-solid/FeatureStore";

import { api } from "..";

import { Booking, useBookings } from "./bookings";
import { Membership, useMemberships } from "./memberships";
import { Organization } from "./organizations";
import { Product, useProducts } from "./products";

export type Activity = {
  type: Activity.Type;
  subjectId: Activity.Subject.Id;
  occurredAt: Date;
  // This is name of user, not a username, don't rename to username
  userName: FullName;
  amount: number;
};

export namespace Activity {
  export type Type = "purchase";
  export type Subject = Booking | Membership | Product;
  export namespace Subject {
    export type Id = Subject["id"];
  }
}

export type ActivitiesRest = Get<{}, { activities: Activity[] }>;

const initialState = {
  byOrgId: {} as Record<Organization.Id, Activity[]>,
};

export const { Context, Provider, useActivities } = FeatureStore.init(
  "Activities",
  initialState,
  ([store, setStore], { withLogger, attachAction }) => {
    const [bookings] = useBookings();
    const [memberships] = useMemberships();
    const [products] = useProducts();

    const accessors = {
      byOrgId: attachAction(
        (id: Organization.Id) =>
          store.byOrgId[id]?.map((activity) =>
            UProxy.merge(activity, {
              get subject() {
                const { subjectId } = activity;

                if (Booking.Id.is(subjectId)) {
                  return bookings.byId(subjectId);
                }
                if (Membership.Id.is(subjectId)) {
                  return memberships.byId(subjectId);
                }
                if (Product.Id.is(subjectId)) {
                  return products.byId(subjectId);
                }
                throw new UnreachableCaseError();
              },
            }),
          ),
        (id) => actions.fetchActivities(id),
        (id) => (res) => !id || res,
      ),
    };

    const actions = withLogger("action", {
      fetchActivities(id: Organization.Id) {
        api.organizations[id]!.activities.GET({}).then(({ activities }) => {
          setStore("byOrgId", id, activities);
        });
      },
    });

    return { accessors, actions };
  },
);
